import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { FiCalendar, FiTrendingUp, FiClock, FiPackage } from 'react-icons/fi';

const TABS = [
  { key: 'daily', label: 'Daily' },
  { key: 'monthly', label: 'Monthly' },
  { key: 'range', label: 'Date Range' },
  { key: 'pending', label: 'Pending' },
  { key: 'products', label: 'Products' },
];

const todayStr = () => new Date().toISOString().split('T')[0];

export default function Reports() {
  const [tab, setTab] = useState('daily');
  const [loading, setLoading] = useState(false);

  // Daily
  const [dailyDate, setDailyDate] = useState(todayStr());
  const [dailyReport, setDailyReport] = useState(null);

  // Monthly
  const [year, setYear] = useState(new Date().getFullYear());
  const [monthlyReport, setMonthlyReport] = useState(null);

  // Range
  const [startDate, setStartDate] = useState(todayStr());
  const [endDate, setEndDate] = useState(todayStr());
  const [rangeReport, setRangeReport] = useState(null);

  // Pending
  const [pendingReport, setPendingReport] = useState(null);

  // Products
  const [productsReport, setProductsReport] = useState(null);

  const fetchDaily = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/reports/daily', { params: { date: dailyDate } });
      setDailyReport(r.data.report);
    } catch { toast.error('Failed to load daily report'); }
    setLoading(false);
  }, [dailyDate]);

  const fetchMonthly = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/reports/monthly', { params: { year } });
      setMonthlyReport(r.data.report);
    } catch { toast.error('Failed to load monthly report'); }
    setLoading(false);
  }, [year]);

  const fetchRange = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    try {
      const r = await api.get('/reports/range', { params: { startDate, endDate } });
      setRangeReport(r.data.report);
    } catch { toast.error('Failed to load range report'); }
    setLoading(false);
  }, [startDate, endDate]);

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/reports/pending');
      setPendingReport(r.data);
    } catch { toast.error('Failed to load pending report'); }
    setLoading(false);
  }, []);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/reports/products');
      setProductsReport(r.data);
    } catch { toast.error('Failed to load product report'); }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (tab === 'daily') fetchDaily();
    else if (tab === 'monthly') fetchMonthly();
    else if (tab === 'range') fetchRange();
    else if (tab === 'pending') fetchPending();
    else if (tab === 'products') fetchProducts();
  }, [tab, fetchDaily, fetchMonthly, fetchRange, fetchPending, fetchProducts]);

  const statusBadge = (status) => {
    const map = { paid: 'badge-success', pending: 'badge-warning', partial: 'badge-info' };
    return <span className={`badge ${map[status] || 'badge-gray'}`}>{status}</span>;
  };

  return (
    <div>
      <div className="filter-tabs" style={{ marginBottom: 20 }}>
        {TABS.map(t => (
          <button key={t.key} className={`filter-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </div>

      {/* DAILY */}
      {tab === 'daily' && (
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-body" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <FiCalendar size={16} color="#94a3b8" />
              <input type="date" className="form-control" style={{ width: 200 }}
                value={dailyDate} onChange={e => setDailyDate(e.target.value)} />
            </div>
          </div>

          {loading ? <div className="loading"><div className="spinner"></div></div> : dailyReport && (
            <>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-value">{dailyReport.billsCount}</div>
                  <div className="stat-label">Bills Created</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">SAR {dailyReport.totalRevenue.toFixed(0)}</div>
                  <div className="stat-label">Total Revenue ({dailyReport.date})</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value" style={{ color: '#10b981' }}>SAR {dailyReport.totalCollected.toFixed(0)}</div>
                  <div className="stat-label">Collected</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value" style={{ color: '#ef4444' }}>SAR {dailyReport.totalPending.toFixed(0)}</div>
                  <div className="stat-label">Pending</div>
                </div>
              </div>

              <div className="card">
                <div className="card-header"><h3 className="card-title">Bills on {dailyReport.date}</h3></div>
                <div className="table-container">
                  <table>
                    <thead><tr><th>Bill #</th><th>Customer</th><th>Total</th><th>Advance</th><th>Remaining</th><th>Status</th></tr></thead>
                    <tbody>
                      {dailyReport.bills.length === 0 && (
                        <tr><td colSpan={6}><div className="empty-state"><h3>No bills on this date</h3></div></td></tr>
                      )}
                      {dailyReport.bills.map(b => (
                        <tr key={b._id}>
                          <td style={{ fontWeight: 600 }}>#{b.billNumber}</td>
                          <td>{b.customerName}</td>
                          <td>SAR {b.total.toFixed(2)}</td>
                          <td>SAR {b.advancePaid.toFixed(2)}</td>
                          <td style={{ color: b.remaining > 0 ? '#ef4444' : '#10b981' }}>SAR {b.remaining.toFixed(2)}</td>
                          <td>{statusBadge(b.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* MONTHLY */}
      {tab === 'monthly' && (
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-body" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <FiTrendingUp size={16} color="#94a3b8" />
              <label className="form-label" style={{ marginBottom: 0 }}>Year</label>
              <input type="number" className="form-control" style={{ width: 120 }}
                value={year} onChange={e => setYear(e.target.value)} />
            </div>
          </div>
          {loading ? <div className="loading"><div className="spinner"></div></div> : monthlyReport && (
            <div className="card">
              <div className="card-header"><h3 className="card-title">Monthly Revenue — {year}</h3></div>
              <div className="table-container">
                <table>
                  <thead><tr><th>Month</th><th>Bills</th><th>Revenue</th><th>Collected</th><th>Pending</th></tr></thead>
                  <tbody>
                    {monthlyReport.map(m => (
                      <tr key={m.month}>
                        <td style={{ fontWeight: 600 }}>{m.month}</td>
                        <td>{m.billsCount}</td>
                        <td>SAR {m.revenue.toFixed(2)}</td>
                        <td style={{ color: '#10b981' }}>SAR {m.collected.toFixed(2)}</td>
                        <td style={{ color: m.pending > 0 ? '#ef4444' : '#10b981' }}>SAR {m.pending.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* RANGE */}
      {tab === 'range' && (
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-body" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <FiCalendar size={16} color="#94a3b8" />
              <input type="date" className="form-control" style={{ width: 180 }}
                value={startDate} onChange={e => setStartDate(e.target.value)} />
              <span style={{ color: '#94a3b8' }}>to</span>
              <input type="date" className="form-control" style={{ width: 180 }}
                value={endDate} onChange={e => setEndDate(e.target.value)} />
              <button className="btn btn-accent" onClick={fetchRange}>Apply</button>
            </div>
          </div>

          {loading ? <div className="loading"><div className="spinner"></div></div> : rangeReport && (
            <>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-value">{rangeReport.billsCount}</div>
                  <div className="stat-label">Bills ({rangeReport.startDate} - {rangeReport.endDate})</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">SAR {rangeReport.totalRevenue.toFixed(0)}</div>
                  <div className="stat-label">Total Revenue</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value" style={{ color: '#10b981' }}>SAR {rangeReport.totalCollected.toFixed(0)}</div>
                  <div className="stat-label">Collected</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value" style={{ color: '#ef4444' }}>SAR {rangeReport.totalPending.toFixed(0)}</div>
                  <div className="stat-label">Pending</div>
                </div>
              </div>
              <div className="card">
                <div className="card-header"><h3 className="card-title">Bills</h3></div>
                <div className="table-container">
                  <table>
                    <thead><tr><th>Bill #</th><th>Customer</th><th>Date</th><th>Total</th><th>Remaining</th><th>Status</th></tr></thead>
                    <tbody>
                      {rangeReport.bills.length === 0 && (
                        <tr><td colSpan={6}><div className="empty-state"><h3>No bills in this range</h3></div></td></tr>
                      )}
                      {rangeReport.bills.map(b => (
                        <tr key={b._id}>
                          <td style={{ fontWeight: 600 }}>#{b.billNumber}</td>
                          <td>{b.customerName}</td>
                          <td>{new Date(b.billDate).toLocaleDateString('en-AE', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                          <td>SAR {b.total.toFixed(2)}</td>
                          <td style={{ color: b.remaining > 0 ? '#ef4444' : '#10b981' }}>SAR {b.remaining.toFixed(2)}</td>
                          <td>{statusBadge(b.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* PENDING */}
      {tab === 'pending' && (
        <div>
          {loading ? <div className="loading"><div className="spinner"></div></div> : pendingReport && (
            <>
              <div className="alert alert-warning" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FiClock size={16} />
                <span><strong>{pendingReport.bills.length}</strong> bill(s) pending — Total due: <strong>SAR {pendingReport.totalPending.toFixed(2)}</strong></span>
              </div>
              <div className="card">
                <div className="table-container">
                  <table>
                    <thead><tr><th>Bill #</th><th>Customer</th><th>Phone</th><th>Total</th><th>Advance</th><th>Remaining</th><th>Status</th></tr></thead>
                    <tbody>
                      {pendingReport.bills.length === 0 && (
                        <tr><td colSpan={7}><div className="empty-state"><h3>🎉 No pending payments</h3></div></td></tr>
                      )}
                      {pendingReport.bills.map(b => (
                        <tr key={b._id}>
                          <td style={{ fontWeight: 600 }}>#{b.billNumber}</td>
                          <td>{b.customerName}</td>
                          <td>{b.customerPhone}</td>
                          <td>SAR {b.total.toFixed(2)}</td>
                          <td style={{ color: '#10b981' }}>SAR {b.advancePaid.toFixed(2)}</td>
                          <td style={{ color: '#ef4444', fontWeight: 600 }}>SAR {b.remaining.toFixed(2)}</td>
                          <td>{statusBadge(b.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* PRODUCTS */}
      {tab === 'products' && (
        <div>
          {loading ? <div className="loading"><div className="spinner"></div></div> : productsReport && (
            <>
              {productsReport.lowStock.length > 0 && (
                <div className="alert alert-danger" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FiPackage size={16} />
                  <span><strong>{productsReport.lowStock.length}</strong> product(s) low on stock</span>
                </div>
              )}
              <div className="card">
                <div className="card-header"><h3 className="card-title">Product Sales Summary</h3></div>
                <div className="table-container">
                  <table>
                    <thead><tr><th>Product</th><th>Category</th><th>Price</th><th>Stock</th><th>Units Sold</th><th>Revenue</th></tr></thead>
                    <tbody>
                      {productsReport.products.length === 0 && (
                        <tr><td colSpan={6}><div className="empty-state"><h3>No products yet</h3></div></td></tr>
                      )}
                      {productsReport.products.map(p => {
                        const sales = productsReport.productSales[p._id] || { quantity: 0, revenue: 0 };
                        const isLow = p.stockQty <= p.minAlertQty;
                        return (
                          <tr key={p._id}>
                            <td style={{ fontWeight: 600 }}>{p.name}</td>
                            <td><span className="badge badge-gray">{p.category}</span></td>
                            <td>SAR {p.price.toFixed(2)}</td>
                            <td style={{ color: isLow ? '#ef4444' : '#10b981', fontWeight: 600 }}>{p.stockQty}</td>
                            <td>{sales.quantity}</td>
                            <td>SAR {sales.revenue.toFixed(2)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}